<template>
    <Button
        size="Small"
        variant="Neutral-Light"
        :icon-description="iconDescription"
        :status="status"
        @click="copyToClipBoard"
    >
        <template #icon>
            <CopyLinkIcon class="size-4.5" />
        </template>
    </Button>
</template>

<script setup lang="ts">
import { $t } from '@/plugins/fluent';
import { stripBidiTokens } from '@/utils/text';
import { computed, provide, ref } from 'vue';
import { ButtonStatus } from '../Button/Button.types';
import Button from '../Button/Button.vue';
import CopyLinkIcon from '../Icon/CopyLinkIcon.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';

type CopyLinkButtonProps = {
    linkTitle: string;
    linkTarget: string;
};

const props = defineProps<CopyLinkButtonProps>();

const status = ref<ButtonStatus>('Default');

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

async function copyToClipBoard() {
    const url = props.linkTarget;
    const title = props.linkTitle !== '' ? stripBidiTokens(props.linkTitle) : $t('link-without-title');
    const html = `<a href="${url}">${title}</a>`;

    const blob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([url], { type: 'text/plain' });

    const clipboardItem = new ClipboardItem({
        'text/html': blob,
        'text/plain': textBlob,
    });

    try {
        await navigator.clipboard.write([clipboardItem]);
        status.value = 'Success';
    } catch {
        status.value = 'Error';
        throw new Error(`Copying ${props.linkTarget} to clipboard failed`);
    } finally {
        triggerHideTooltip();
    }
}

const iconDescription = computed(() => {
    switch (status.value) {
        case 'Default':
            return $t('copy-link-button-copy-to-clipboard');
        case 'Success':
            return $t('copy-link-button-copy-to-clipboard-success');
        case 'Error':
            return $t('copy-link-button-copy-to-clipboard-error');
    }
});
</script>
